import { DOCUMENT, Location } from '@angular/common';
import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostBinding,
    Inject,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewRef
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { OnChange } from '@df/ng/on-change';
import { Renderer } from '@df/ng/renderer';
import { injectTimeoutService, TimeoutService } from '@df/ng/timeout.service';
import { BodyClassService } from '@df/ui/body-class.service';
import { blockStatus } from '@df/ui/modal/block-status';
import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subscription } from 'rxjs/internal/Subscription';

export const CAN_SHOW_EVENT = 'toggle-block.canShow';

export interface ICanShowEvent {
    name?: string;
    canShow: boolean;
}

/**
 * Abstract toogle block class.
 *
 * ```typescript
 * export class Component extends ToggleBlockAbstact implements OnInit {
 *   @Input() name: string = 'component-name';
 *
 *   ngOnInit() {
 *     this.bodyClassName = `component-open-${this.name}`;
 *
 *     this.showEvents = [`'component-${this.name}.open.request`, `component-${this.name}.toggle.request`];
 *     this.hideEvents = [`component-${this.name}.close.request`, `component-${this.name}.toggle.request`, NavigationStart];
 *
 *     super.ngOnInit();
 *   }
 * }
 * ```
 *
 * @export
 * @abstract
 * @class ToggleBlockAbstact
 * @implements {OnChanges}
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @deprecated Please avoid using this service anywhere
 */
@Directive()
export abstract class ToggleBlockAbstact implements OnChanges, OnInit, OnDestroy {
    protected showEvents?: any[];
    protected hideEvents?: any[];
    protected forbiddenLocations?: string[];
    protected bodyClassName?: string | string[];

    /** @internal */
    protected _closeTimeout?: number;
    /** @internal */
    protected _subscriptions = new Subscription();

    visible$ = new BehaviorSubject<boolean>(false);

    public wasEverVisible = false;

    /**
     * Since we often hide thing using hide-left (like for off canvas)
     * images are considered to be in viewport (as transition does not affect it)
     *
     * To prevent images loading until the component is actually visible we use this class
     * to hide all images inside
     *
     * This value is set to true in constructor of child method
     */
    @HostBinding('class.hide-images')
    wasNeverVisible = false;

    /**
     * Visibility state.
     *
     * @type {boolean}
     * @memberof ToggleBlockAbstact
     */
    @OnChange(function () {
        if (this.isVisible) {
            this.wasNeverVisible = false;
        }
    })
    isVisible = false;

    /**
     * An optional name.
     *
     * @type {string}
     */
    @Input()
    protected abstract name: string;

    /**
     * An optional auto close delay.
     *
     * @type {number}
     * @memberof ToggleBlockAbstact
     */
    @Input()
    autoClose?: number;

    /**
     * If defined we will only allow showing one in group at the same time.
     * If another one will try to appear we will wait for previous to hide.
     *
     * @type {boolean}
     * @memberof ToggleBlockAbstact
     */
    @Input()
    blockGroup?: string;

    /**
     * If `true` we will not add/remove ng-hide class on show/hide.
     *
     * @type {boolean}
     * @memberof ToggleBlockAbstact
     */
    @Input()
    manualToggle = false;

    /**
     * Set to true to remove from prerender.
     *
     * @type {boolean}
     * @memberof ToggleBlockAbstact
     */
    @Input()
    noSeo?: boolean;

    private bodyClassService = inject(BodyClassService);
    protected location = inject(Location);
    protected timeoutService = injectTimeoutService();

    protected toggleThrottled: (...args) => void;

    constructor(
        protected el: ElementRef,
        private cd: ChangeDetectorRef,
        protected renderer: Renderer,
        protected meta: Meta,
        protected events: Events,
        readonly client: Client,
        readonly app: App,
        protected router: Router,
        @Inject(DOCUMENT) protected document: Document
    ) {
        this.toggleThrottled = this.timeoutService.throttle((...args) => this.toggle(args), 300);
        cd.detach();
        this.toggleThrottled = this.timeoutService.throttle((...args) => this.toggle(args), 300);
    }

    ngOnChanges() {
        if (this.client.isPrerender && this.noSeo) {
            this.renderer.remove(this.el);
        }
    }

    ngOnInit() {
        if (!this.bodyClassName) {
            this.bodyClassName = `${this.name}-open`;
        }

        if (!this.showEvents) {
            this.showEvents = [`${this.name}.open.request`, `${this.name}.toggle.request`];
        }

        if (!this.hideEvents) {
            this.hideEvents = [`${this.name}.close.request`, `${this.name}.toggle.request`, NavigationStart];
        }

        const toggleEvents = intersection(this.showEvents, this.hideEvents);
        const showEvents = difference(this.showEvents, toggleEvents);
        const hideEvents = difference(this.hideEvents, toggleEvents);

        // assign events
        showEvents.forEach(e => {
            if (typeof e === 'string') {
                this._subscriptions.add(this.events.on(e).subscribe((...args) => this.show(...args)));
            } else if (this.isRouterEventClass(e)) {
                this._subscriptions.add(this.router.events.subscribe(routerEvent => routerEvent instanceof e && this.show()));
            }
        });

        hideEvents.forEach(e => {
            if (typeof e === 'string') {
                this._subscriptions.add(this.events.on(e).subscribe((...args) => this.hide(...args)));
            } else if (this.isRouterEventClass(e)) {
                this._subscriptions.add(this.router.events.subscribe(routerEvent => routerEvent instanceof e && this.hide()));
            }
        });

        toggleEvents.forEach(e => {
            if (typeof e === 'string') {
                this._subscriptions.add(this.events.on(e).subscribe((...args) => this.toggleThrottled(...args)));
            } else if (this.isRouterEventClass(e)) {
                this._subscriptions.add(this.router.events.subscribe(routerEvent => routerEvent instanceof e && this.toggleThrottled()));
            }
        });

        this._subscriptions.add(
            this.visible$.subscribe((to: boolean) => {
                this.isVisible = to;
                this.detectChanges();
            })
        );
    }

    ngOnDestroy() {
        this.hide(false);

        this._subscriptions.unsubscribe();
    }

    /**
     * Show element.
     *
     * @param {*} args
     * @memberof ToggleBlockAbstact
     */
    show(...args) {
        clearTimeout(this._closeTimeout);

        if (!this.isVisible) {
            if (this.canShow()) {
                blockStatus.groupShow(
                    this,
                    this.blockGroup,
                    false,
                    () => {
                        this.isVisible = true;
                        this.visible$.next(true);

                        if (!this.manualToggle) {
                            this.renderer.removeClass(this.el.nativeElement, 'ng-hide');
                        }

                        this.onShow();

                        this.applyBodyClass();

                        // if(!vm.noScrollTo) {
                        //     // close any dialogs
                        //     ngDialog.close();
                        //     // scroll to top
                        //     $scope.$root.scrollTo(0)
                        // }
                        if (this.autoClose) {
                            this._closeTimeout = this.timeoutService.setTimeout(() => this.hide(), this.autoClose);
                        }

                        // app.event.broadcast('image.update.request');

                        // always detect changes, you do not know if the component is set to `ChangeDetectionStrategy.OnPush` or not
                        this.detectChanges();

                        return true;
                    },
                    () => {
                        this.hide();
                        this.onHideByGroup();
                    }
                );
            }
        } else if (this.autoClose) {
            this._closeTimeout = this.timeoutService.setTimeout(() => this.hide(), this.autoClose);
        }
    }

    /**
     * Called when groupService hide this block
     * this happens when another component forces opening itself
     * we may need to reset something here to retry showing again
     */
    protected onHideByGroup() {}

    /**
     * Hide element.
     *
     * @memberof ToggleBlockAbstact
     */
    hide(detectChanges = true) {
        if (this.isVisible) {
            clearTimeout(this._closeTimeout);

            this.visible$.next(false);

            if (!this.manualToggle) {
                this.renderer.addClass(this.el.nativeElement, 'ng-hide');
            }

            this.applyBodyClass();

            blockStatus.groupCloseNotify(this);

            // always detect changes, you do not know if the component is set to `ChangeDetectionStrategy.OnPush` or not
            // do not detect changes on destroy
            if (detectChanges) {
                this.detectChanges();
            }
        }
    }

    /**
     * Hide or show element.
     *
     * @memberof ToggleBlockAbstact
     */
    toggle(...args) {
        this.isVisible ? this.hide(...args) : this.show(...args);
    }

    /**
     * Check if element can be shown.
     *
     * @returns {boolean}
     * @memberof ToggleBlockAbstact
     */
    canShow(): boolean {
        const eventData: ICanShowEvent = {
            name: this.name,
            canShow: !this.forbiddenLocations?.includes(this.location.path())
        };

        this.events.broadcast(CAN_SHOW_EVENT, eventData);

        return eventData.canShow;
    }

    /**
     * Abstract method called when when we set visibility to true.
     */
    protected onShow() {}

    /**
     * Add class to `body` tag.
     *
     * @protected
     * @memberof ToggleBlockAbstact
     */
    protected applyBodyClass() {
        if (this.bodyClassName) {
            this.bodyClassService.set(this.bodyClassName, this, this.isVisible);
        }
    }

    /**
     * Check if event is one of router event class.
     *
     * @protected
     * @param {Event} e
     * @returns {boolean}
     * @memberof ToggleBlockAbstact
     */
    protected isRouterEventClass(e): boolean {
        return e === NavigationStart || e === NavigationEnd || e === NavigationCancel || e === NavigationError;
    }

    detectChanges(): void {
        if (!(<ViewRef>this.cd).destroyed) {
            this.cd.detectChanges();
        }
    }
}
